export const DEFAULT_PRICING_POLICY = {
    maxSalesDiscountBps: 500,
    maxSalesDiscountAmountMinor: 500000,
    minGrossMarginBps: 0,
    taxInclusiveDisplay: true,
};
function roundDiv(numerator, denominator) {
    if (denominator === 0)
        throw new Error('Cannot divide by zero');
    const sign = numerator < 0 ? -1 : 1;
    return sign * Math.round(Math.abs(numerator) / denominator);
}
function normalize(value) {
    return value.toLowerCase().replace(/[\s\-_/（）()]/g, '');
}
function modelSuffixes(value) {
    const compact = normalize(value ?? '');
    const suffixes = new Set();
    const add = (candidate) => {
        if (candidate.length >= 3 && /[a-z]/.test(candidate) && /\d/.test(candidate))
            suffixes.add(candidate);
    };
    for (let index = 0; index < compact.length; index += 1) {
        if (!/[a-z]/.test(compact[index]))
            continue;
        const match = compact.slice(index).match(/^([a-z]+\d+[a-z0-9]*)/);
        if (match)
            add(match[1]);
    }
    for (let index = 0; index < compact.length; index += 1) {
        if (!/\d/.test(compact[index]))
            continue;
        if (index > 0 && /\d/.test(compact[index - 1]))
            continue;
        const match = compact.slice(index).match(/^(\d+[a-z]+[a-z0-9]*)/);
        if (match)
            add(match[1]);
    }
    return [...suffixes];
}
export function findProductForItem(item, products) {
    if (item.productId)
        return products.find((product) => product.id === item.productId);
    const haystack = normalize(item.rawText);
    const candidates = products
        .map((product) => {
        const suffixes = [product.model, product.sku, ...product.aliases]
            .flatMap((value) => modelSuffixes(value));
        const generatedAliases = [...new Set([
                ...suffixes,
                ...suffixes.map((suffix) => product.brand ? `${normalize(product.brand)}${suffix}` : ''),
            ])].filter((term) => term.length > 1);
        const terms = [product.name, product.sku, product.model, product.specification, ...generatedAliases, ...product.aliases]
            .filter((term) => Boolean(term))
            .map(normalize)
            .filter(Boolean);
        const score = terms.reduce((result, term) => {
            if (haystack.includes(term))
                return result + term.length;
            if (term.includes(haystack) && haystack.length >= 2)
                return result + haystack.length;
            return result;
        }, 0);
        return { product, score };
    })
        .filter((candidate) => candidate.score > 0)
        .sort((left, right) => right.score - left.score);
    return candidates[0]?.product;
}
export function quantityDiscountBps(product, quantity) {
    return product.discountTiers
        .filter((tier) => quantity >= tier.minQuantity)
        .sort((left, right) => right.minQuantity - left.minQuantity)[0]?.discountBps ?? 0;
}
export function resolveProductPrice(product, quantity, priceLevel = 'market_guide', vendorPolicies = []) {
    const tierDiscount = quantityDiscountBps(product, quantity);
    const vendorPolicy = product.vendorPolicyId
        ? vendorPolicies?.find((policy) => policy.id === product.vendorPolicyId)
        : vendorPolicies?.find((policy) => policy.vendorName === product.vendorName);
    if (product.tablePriceMinor !== undefined && vendorPolicy) {
        const realCostMinor = roundDiv(product.tablePriceMinor * (10000 - vendorPolicy.costDiscountBps), 10000);
        const markupBps = priceLevel === 'factory'
            ? vendorPolicy.factoryMarkupBps
            : vendorPolicy.marketGuideMarkupBps;
        const unitPriceMinor = roundDiv(realCostMinor * (10000 + markupBps), 10000);
        return {
            unitPriceMinor,
            unitCostMinor: realCostMinor,
            discountBps: tierDiscount,
            trace: [
                `厂商表价 ${product.tablePriceMinor} 分，${vendorPolicy.vendorName}进价下浮 ${vendorPolicy.costDiscountBps / 100}%`,
                `真实进价 ${realCostMinor} 分`,
                priceLevel === 'factory'
                    ? `出厂价按真实进价加 ${markupBps / 100}%`
                    : `市场指导价按真实进价加 ${markupBps / 100}%`,
                tierDiscount > 0 ? `数量 ${quantity} 命中阶梯折扣 ${tierDiscount / 100}%` : '未命中数量阶梯折扣',
            ],
        };
    }
    if (product.pricing.kind === 'fixed') {
        return {
            unitPriceMinor: product.pricing.unitPriceMinor,
            unitCostMinor: product.pricing.unitCostMinor,
            discountBps: tierDiscount,
            trace: [
                `产品 ${product.sku} 使用客户基准价 ${product.pricing.unitPriceMinor} 分`,
                tierDiscount > 0 ? `数量 ${quantity} 命中阶梯折扣 ${tierDiscount / 100}%` : '未命中数量阶梯折扣',
            ],
        };
    }
    const unitPriceMinor = roundDiv(product.pricing.unitCostMinor * (10000 + product.pricing.markupBps), 10000);
    return {
        unitPriceMinor,
        unitCostMinor: product.pricing.unitCostMinor,
        discountBps: tierDiscount,
        trace: [
            `产品 ${product.sku} 使用成本加成：${product.pricing.unitCostMinor} 分 × ${1 + product.pricing.markupBps / 10000}`,
            tierDiscount > 0 ? `数量 ${quantity} 命中阶梯折扣 ${tierDiscount / 100}%` : '未命中数量阶梯折扣',
        ],
    };
}
export function calculateTaxInclusive(amountMinor, taxRateBps) {
    const taxAmountMinor = roundDiv(amountMinor * taxRateBps, 10000 + taxRateBps);
    return {
        netAmountMinor: amountMinor - taxAmountMinor,
        taxAmountMinor,
        totalAmountMinor: amountMinor,
    };
}
function productLine(item, product, quoteDiscountBps, priceLevel, vendorPolicies) {
    const pricing = resolveProductPrice(product, item.quantity, priceLevel, vendorPolicies);
    const optionPriceMinor = (product.options ?? []).reduce((sum, option) => sum + (typeof option === 'string' ? 0 : option.priceMinor ?? 0), 0);
    const baseUnitPriceMinor = pricing.unitPriceMinor;
    const unitPriceMinor = baseUnitPriceMinor + optionPriceMinor;
    const discountBps = Math.min(10000, pricing.discountBps + quoteDiscountBps);
    const grossAmountMinor = unitPriceMinor * item.quantity;
    const totalAmountMinor = roundDiv(grossAmountMinor * (10000 - discountBps), 10000);
    const tax = calculateTaxInclusive(totalAmountMinor, product.taxRateBps);
    return {
        id: crypto.randomUUID(),
        kind: 'product',
        productId: product.id,
        name: product.name,
        brand: product.brand ?? product.vendorName,
        model: product.model ?? product.sku,
        specification: product.specification,
        configuration: product.configuration ?? [],
        options: product.options ?? [],
        parameters: product.parameters ?? [],
        imagePath: product.imagePath,
        unit: product.unit,
        quantity: item.quantity,
        baseUnitPriceMinor,
        optionPriceMinor,
        unitPriceMinor,
        unitCostMinor: pricing.unitCostMinor,
        discountBps,
        taxRateBps: product.taxRateBps,
        ...tax,
        confidence: item.confidence,
        status: item.confidence < 0.75 ? 'needs_review' : 'calculated',
        ruleTrace: [
            ...pricing.trace,
            optionPriceMinor > 0 ? `选配加价 ${optionPriceMinor} 分/台` : '无选配加价',
            quoteDiscountBps > 0 ? `报价单额外折扣 ${quoteDiscountBps / 100}%` : '报价单无额外折扣',
            `含税金额按税率 ${product.taxRateBps / 100}% 拆分`,
        ],
        sourceRefs: item.sourceRefs,
    };
}
function missingProductLine(item) {
    if (item.quotedUnitPriceMinor !== undefined) {
        const tax = calculateTaxInclusive(item.quotedUnitPriceMinor * item.quantity, 1300);
        return {
            id: crypto.randomUUID(),
            kind: 'manual',
            name: item.productName || item.rawText,
            model: item.rawText,
            unit: item.unit || '台',
            quantity: item.quantity,
            baseUnitPriceMinor: item.quotedUnitPriceMinor,
            marketGuideUnitPriceMinor: item.quotedUnitPriceMinor,
            factoryUnitPriceMinor: item.quotedUnitPriceMinor,
            marketGuideTotalAmountMinor: tax.totalAmountMinor,
            factoryTotalAmountMinor: tax.totalAmountMinor,
            unitPriceMinor: item.quotedUnitPriceMinor,
            discountBps: 0,
            taxRateBps: 1300,
            ...tax,
            confidence: item.confidence,
            status: 'needs_review',
            ruleTrace: ['使用需求框中人工录入的价格', '该型号未在已发布价格库中唯一匹配，必须由报价管理员复核后使用'],
            sourceRefs: item.sourceRefs,
        };
    }
    return {
        id: crypto.randomUUID(),
        kind: 'product',
        name: item.productName || item.rawText,
        model: item.rawText,
        unit: item.unit || '项',
        quantity: item.quantity,
        unitPriceMinor: 0,
        discountBps: 0,
        netAmountMinor: 0,
        taxRateBps: 0,
        taxAmountMinor: 0,
        totalAmountMinor: 0,
        confidence: item.confidence,
        status: 'needs_price',
        ruleTrace: ['未在已发布价格库中找到唯一产品，禁止 AI 猜测价格'],
        sourceRefs: item.sourceRefs,
    };
}
function serviceLine(rule, quantity, distanceKm, sourceRefs) {
    if (rule.kind === 'manual') {
        const total = rule.defaultMinor ?? 0;
        const tax = calculateTaxInclusive(total, rule.taxRateBps);
        return {
            id: crypto.randomUUID(),
            kind: 'manual',
            name: rule.name,
            unit: '项',
            quantity: 1,
            unitPriceMinor: total,
            discountBps: 0,
            taxRateBps: rule.taxRateBps,
            ...tax,
            confidence: rule.defaultMinor ? 0.7 : 0,
            status: rule.defaultMinor ? 'needs_review' : 'needs_price',
            ruleTrace: ['非标服务需要报价管理员确认，默认金额仅作为待审草稿'],
            sourceRefs,
        };
    }
    const unitPriceMinor = rule.kind === 'per_unit'
        ? rule.unitPriceMinor
        : rule.baseMinor + Math.max(0, distanceKm - rule.includedKm) * rule.perKmMinor;
    const quantityForAmount = rule.kind === 'per_unit' ? quantity : 1;
    const total = unitPriceMinor * quantityForAmount;
    const tax = calculateTaxInclusive(total, rule.taxRateBps);
    return {
        id: crypto.randomUUID(),
        kind: 'service',
        name: rule.name,
        unit: rule.kind === 'per_unit' ? '台/套' : '项',
        quantity: quantityForAmount,
        unitPriceMinor,
        discountBps: 0,
        taxRateBps: rule.taxRateBps,
        ...tax,
        confidence: 1,
        status: 'calculated',
        ruleTrace: rule.kind === 'per_unit'
            ? [`服务规则 ${rule.id}：单价 ${rule.unitPriceMinor} 分 × ${quantityForAmount}`]
            : [`服务规则 ${rule.id}：${rule.baseMinor} 分 + ${Math.max(0, distanceKm - rule.includedKm)}km × ${rule.perKmMinor} 分`],
        sourceRefs,
    };
}
function marginBps(line) {
    if (line.unitCostMinor === undefined || line.totalAmountMinor === 0)
        return undefined;
    const revenue = line.totalAmountMinor;
    const cost = line.unitCostMinor * line.quantity;
    return roundDiv((revenue - cost) * 10000, revenue);
}
export function calculateQuote(input) {
    const policy = { ...DEFAULT_PRICING_POLICY, ...input.policy };
    const priceLevel = input.priceLevel ?? 'market_guide';
    const quoteDiscountBps = input.quoteDiscountBps ?? 0;
    const lines = [];
    const approvalRequirements = new Set();
    const approvalReasons = [];
    const unresolvedQuestions = [];
    if (input.requirement.items.length === 0) {
        unresolvedQuestions.push('请提供客户需要的设备型号、数量和交付地点。');
        approvalRequirements.add('pricing_admin');
        approvalReasons.push('需求中尚未识别出可核价的设备项目');
    }
    for (const item of input.requirement.items) {
        const product = findProductForItem(item, input.catalog.products);
        if (!product || product.effectiveFrom > new Date().toISOString() || (product.effectiveTo && product.effectiveTo < new Date().toISOString())) {
            lines.push(missingProductLine(item));
            approvalRequirements.add('pricing_admin');
            approvalReasons.push(`“${item.rawText}”缺少可用的已发布价格`);
            unresolvedQuestions.push(`请确认“${item.rawText}”对应的产品型号和有效价格。`);
            continue;
        }
        const line = productLine(item, product, quoteDiscountBps, priceLevel, input.catalog.vendorPolicies);
        lines.push(line);
        const lineMargin = marginBps(line);
        if (line.status === 'needs_review') {
            approvalRequirements.add('pricing_admin');
            approvalReasons.push(`“${line.name}”识别置信度低于 75%，需要复核`);
        }
        if (line.discountBps > policy.maxSalesDiscountBps) {
            approvalRequirements.add('boss');
            approvalReasons.push(`“${line.name}”折扣 ${line.discountBps / 100}% 超过销售审批上限`);
        }
        if (lineMargin !== undefined && lineMargin < policy.minGrossMarginBps) {
            approvalRequirements.add('boss');
            approvalReasons.push(`“${line.name}”毛利率低于系统下限`);
        }
    }
    const sourceRefs = input.requirement.sourceRefs;
    const totalQuantity = input.requirement.items.reduce((sum, item) => sum + item.quantity, 0);
    const hasCompressor = lines.some((line) => line.kind === 'product' && line.status !== 'needs_price');
    if (input.requirement.services.installationRequired) {
        const rule = input.catalog.serviceRules.find((candidate) => candidate.kind === 'per_unit' && candidate.id === 'service-installation');
        if (rule)
            lines.push(serviceLine(rule, totalQuantity, input.requirement.services.distanceKm ?? 0, sourceRefs));
    }
    if (input.requirement.services.commissioningRequired) {
        const rule = input.catalog.serviceRules.find((candidate) => candidate.kind === 'per_unit' && candidate.id === 'service-commissioning');
        if (rule)
            lines.push(serviceLine(rule, totalQuantity, input.requirement.services.distanceKm ?? 0, sourceRefs));
    }
    if (hasCompressor && input.requirement.services.distanceKm !== undefined) {
        const rule = input.catalog.serviceRules.find((candidate) => candidate.kind === 'distance');
        if (rule)
            lines.push(serviceLine(rule, 1, input.requirement.services.distanceKm, sourceRefs));
    }
    else if (hasCompressor) {
        unresolvedQuestions.push('请确认项目现场距离，以便计算运费。');
        approvalRequirements.add('pricing_admin');
        approvalReasons.push('缺少现场距离，运费无法自动核算');
    }
    if (input.requirement.services.specialRequirements) {
        const manualRules = input.catalog.serviceRules.filter((rule) => rule.kind === 'manual');
        for (const rule of manualRules) {
            if (normalize(input.requirement.services.specialRequirements).includes(normalize(rule.serviceType))) {
                lines.push(serviceLine(rule, 1, input.requirement.services.distanceKm ?? 0, sourceRefs));
                approvalRequirements.add('pricing_admin');
                approvalReasons.push(`非标服务“${rule.name}”需要报价管理员核价`);
            }
        }
    }
    const subtotalMinor = lines.reduce((sum, line) => sum + line.netAmountMinor, 0);
    const taxMinor = lines.reduce((sum, line) => sum + line.taxAmountMinor, 0);
    const totalMinor = lines.reduce((sum, line) => sum + line.totalAmountMinor, 0);
    return {
        lines,
        subtotalMinor,
        taxMinor,
        totalMinor,
        priceLevel,
        policy,
        approvalRequirements: [...approvalRequirements],
        approvalReasons: [...new Set(approvalReasons)],
        unresolvedQuestions: [...new Set(unresolvedQuestions)],
    };
}
//# sourceMappingURL=pricing.js.map